<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/logbook/testing/field">Field Tests</a></li>
        <li class="breadcrumb-item active">{{ isEditMode ? 'Edit' : 'Upload' }} Custom Test</li>
      </ol>
    </nav>
    <h1 class="h2 mb-0 fw-bold">{{ isEditMode ? 'Edit Custom Test' : 'Upload Custom Test' }}</h1>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="isLoading" class="text-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="text-muted mt-2">Loading test data...</p>
</div>

<!-- Form -->
<form *ngIf="!isLoading" [formGroup]="testForm" (ngSubmit)="onSubmit()">
  <div class="row g-4">
    <!-- Left Column: Test Details -->
    <div class="col-lg-8">
      <!-- File Upload Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-cloud-upload me-2"></i>Upload Files</h5>
        </div>
        <div class="card-body">
          <!-- Drop Zone -->
          <div class="upload-dropzone"
               [class.drag-over]="isDragOver"
               [class.has-files]="uploadedFiles.length > 0"
               (dragover)="onDragOver($event)"
               (dragleave)="onDragLeave($event)"
               (drop)="onDrop($event)">
            <input type="file" id="fileInput" class="d-none"
                   accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                   multiple
                   (change)="onFileSelect($event)">

            <div class="dropzone-content" *ngIf="uploadedFiles.length === 0">
              <i class="bi bi-cloud-arrow-up"></i>
              <h5>Drop files here</h5>
              <p class="text-muted mb-3">or click to browse</p>
              <label for="fileInput" class="btn btn-primary">
                <i class="bi bi-folder2-open me-2"></i>Browse Files
              </label>
              <p class="small text-muted mt-3 mb-0">
                Supports: PDF, Word, JPG, PNG (Max 25MB per file)
              </p>
            </div>

            <!-- Uploaded Files -->
            <div class="w-100" *ngIf="uploadedFiles.length > 0">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="fw-medium">{{ uploadedFiles.length }} file(s) uploaded</span>
                <label for="fileInput" class="btn btn-sm btn-outline-primary">
                  <i class="bi bi-plus-lg me-1"></i>Add More
                </label>
              </div>

              <div class="file-list">
                <div *ngFor="let file of uploadedFiles; let i = index" class="file-item">
                  <div class="file-icon">
                    <i class="bi" [ngClass]="getFileIcon(file.type)"></i>
                  </div>
                  <div class="file-info flex-grow-1">
                    <button *ngIf="file.uploaded && file.filePath" type="button" (click)="openFile(file.filePath)" class="file-name btn btn-link p-0 text-decoration-none text-start">
                      {{ file.name }}
                      <i class="bi bi-box-arrow-up-right ms-1 small"></i>
                    </button>
                    <div *ngIf="!file.uploaded || !file.filePath" class="file-name">{{ file.name }}</div>
                    <div class="file-meta small text-muted">
                      {{ formatFileSize(file.size) }}
                      <span *ngIf="file.uploaded" class="text-success ms-2">
                        <i class="bi bi-check-circle me-1"></i>Uploaded
                      </span>
                    </div>
                    <div class="progress mt-1 h-4px" *ngIf="!file.uploaded">
                      <div class="progress-bar" [style.width.%]="file.uploadProgress"></div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-sm btn-ghost text-danger" (click)="removeFile(i)">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Test Info Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Test Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <!-- Title -->
            <div class="col-12">
              <label class="form-label">Test Title <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="title"
                     placeholder="e.g., DJI Agras T40 Pre-Flight Checklist">
            </div>

            <!-- Test Type -->
            <div class="col-md-6">
              <label class="form-label">Test Type <span class="text-danger">*</span></label>
              <div class="test-type-selector">
                <div *ngFor="let type of testTypes" class="test-type-option"
                     [class.selected]="testForm.get('testType')?.value === type.value"
                     (click)="testForm.patchValue({ testType: type.value })">
                  <i class="bi" [ngClass]="type.icon"></i>
                  <span class="type-label">{{ type.label }}</span>
                </div>
              </div>
            </div>

            <!-- Category -->
            <div class="col-md-6">
              <label class="form-label">Category <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="category">
                <option value="">Select category...</option>
                <option *ngFor="let cat of categories" [value]="cat.value">{{ cat.label }}</option>
              </select>
            </div>

            <!-- Date Performed -->
            <div class="col-md-6">
              <label class="form-label">Date Performed <span class="text-danger">*</span></label>
              <input type="date" class="form-control" formControlName="datePerformed"
                     [max]="today">
              <div *ngIf="isDateInFuture()" class="text-danger small mt-1">
                <i class="bi bi-exclamation-circle me-1"></i>Date cannot be in the future
              </div>
            </div>

            <!-- Description -->
            <div class="col-12">
              <label class="form-label">Description</label>
              <textarea appCharCounter class="form-control" formControlName="description" rows="2"
                        placeholder="Brief description of this test or checklist..."></textarea>
            </div>

            <!-- Applicable Aircraft -->
            <div class="col-md-6">
              <label class="form-label">Applicable Aircraft</label>
              <select class="form-select" formControlName="applicableAircraft">
                <option value="all">All Aircraft</option>
                <option *ngFor="let drone of drones()" [value]="drone.id">
                  {{ drone.name }}
                </option>
              </select>
            </div>

            <!-- Frequency -->
            <div class="col-md-6">
              <label class="form-label">Frequency</label>
              <select class="form-select" formControlName="frequency">
                <option value="as-needed">As Needed</option>
                <option value="every-flight">Every Flight</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="annually">Annually</option>
              </select>
            </div>

            <!-- Requires Signoff -->
            <div class="col-12">
              <div class="form-check">
                <input type="checkbox" class="form-check-input" formControlName="requiresSignoff" id="requiresSignoff">
                <label class="form-check-label" for="requiresSignoff">
                  Requires pilot signature/signoff after completion
                </label>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Summary & Actions -->
    <div class="col-lg-4">
      <!-- Preview Card -->
      <div class="card mb-4 preview-card">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-eye me-2"></i>Preview</h5>
        </div>
        <div class="card-body text-center">
          <div class="preview-icon mb-3">
            <i class="bi" [ngClass]="getTestTypeIcon(testForm.get('testType')?.value)"></i>
          </div>
          <h6 class="mb-1">{{ testForm.get('title')?.value || 'Test Title' }}</h6>
          <div class="d-flex justify-content-center gap-2 mb-3">
            <span class="badge bg-primary" *ngIf="testForm.get('testType')?.value">
              {{ getTestTypeLabel() }}
            </span>
            <span class="badge bg-secondary" *ngIf="testForm.get('category')?.value">
              {{ getCategoryLabel() }}
            </span>
          </div>
          <p class="small text-muted mb-0">
            {{ testForm.get('description')?.value || 'No description provided' }}
          </p>
        </div>
      </div>

      <!-- Summary Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-clipboard-data me-2"></i>Summary</h5>
        </div>
        <div class="card-body">
          <div class="summary-list">
            <div class="summary-item">
              <span class="summary-label">Files Uploaded</span>
              <span class="summary-value">{{ uploadedFiles.length }}</span>
            </div>
            <div class="summary-item" *ngIf="testForm.get('testType')?.value">
              <span class="summary-label">Test Type</span>
              <span class="summary-value">{{ getTestTypeLabel() }}</span>
            </div>
            <div class="summary-item" *ngIf="testForm.get('category')?.value">
              <span class="summary-label">Category</span>
              <span class="summary-value">{{ getCategoryLabel() }}</span>
            </div>
            <div class="summary-item" *ngIf="testForm.get('requiresSignoff')?.value">
              <span class="summary-label">Signoff</span>
              <span class="summary-value text-warning">Required</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Help Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-lightbulb me-2"></i>Tips</h5>
        </div>
        <div class="card-body">
          <ul class="tips-list small">
            <li>Upload PDF checklists for best compatibility</li>
            <li>Use clear, descriptive titles</li>
            <li>Categorize tests for easy filtering</li>
            <li>Enable signoff for compliance tracking</li>
            <li>Add descriptions to help pilots</li>
          </ul>
        </div>
      </div>

      <!-- Actions Card -->
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-save me-2"></i>Actions</h5>
        </div>
        <div class="card-body">
          <div class="d-grid gap-2">
            <button type="submit" class="btn btn-primary"
                    [disabled]="isSaving || testForm.invalid || uploadedFiles.length === 0">
              <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2"></span>
              <i *ngIf="!isSaving" class="bi bi-check-lg me-2"></i>
              {{ isSaving ? 'Saving...' : (isEditMode ? 'Update Test' : 'Save Test') }}
            </button>
            <button type="button" class="btn btn-outline-secondary" (click)="onCancel()">
              Cancel
            </button>
          </div>

          <div class="alert alert-info mt-3 mb-0 small" *ngIf="uploadedFiles.length === 0">
            <i class="bi bi-info-circle me-2"></i>
            Upload at least one file to save.
          </div>
        </div>
      </div>
    </div>
  </div>
</form>
